import { useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { Empty, Spin, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { AIConversationCard } from '@/components/ai-design-system';
import type { AiConversation, AiSavedReport } from '@/services/aiAgent.service';

const { Text } = Typography;

export type SidebarSection = 'workspace' | 'dashboard' | 'reports' | 'shared';

export interface WorkspaceSidebarProps {
  section: SidebarSection;
  onSectionChange: (s: SidebarSection) => void;
  conversations: AiConversation[];
  sharedConversations: Array<{ resourceId: string; title?: string }>;
  reports: AiSavedReport[];
  activeId?: string | null;
  loading?: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onSelectReport?: (id: string) => void;
  pinCount?: number;
  onRename?: (id: string, title: string) => void;
  onPin?: (id: string, pinned: boolean) => void;
  onFavorite?: (id: string, favorite: boolean) => void;
  onArchive?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onShare?: (id: string) => void;
}

const NAV: { key: SidebarSection; label: string; href?: string }[] = [
  { key: 'workspace', label: 'Workspace' },
  { key: 'dashboard', label: 'My Dashboard', href: '/ai/dashboard' },
  { key: 'reports', label: 'Saved UDQs', href: '/ai/udq' },
  { key: 'shared', label: 'Shared' },
];

export function WorkspaceSidebar(props: WorkspaceSidebarProps) {
  const {
    section,
    onSectionChange,
    conversations,
    sharedConversations,
    reports,
    activeId,
    loading,
    searchQuery,
    onSearchChange,
    onNewChat,
    onSelectConversation,
    onSelectReport,
    pinCount,
    onRename,
    onPin,
    onFavorite,
    onArchive,
    onDuplicate,
    onDelete,
    onShare,
  } = props;

  const pinned = conversations.filter((c) => c.pinned);
  const recent = conversations.filter((c) => !c.pinned);
  const list = useMemo(() => [...pinned, ...recent], [pinned, recent]);
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: list.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    overscan: 8,
  });

  return (
    <aside className="ai-workspace-sidebar" aria-label="Conversation history">
      <div style={{ padding: 12, borderBottom: '1px solid var(--ai-border)' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
          {NAV.map((n) =>
            n.href ? (
              <Link key={n.key} to={n.href} style={{ fontSize: 12, fontWeight: 600 }}>
                {n.label}
                {n.key === 'dashboard' && pinCount ? ` (${pinCount})` : ''}
              </Link>
            ) : (
              <button
                key={n.key}
                type="button"
                onClick={() => onSectionChange(n.key)}
                style={{
                  border: 'none',
                  background: section === n.key ? 'rgba(22,119,255,0.1)' : 'transparent',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: section === n.key ? 600 : 400,
                }}
              >
                {n.label}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          onClick={onNewChat}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: 'none',
            background: '#1677ff',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + New Chat
        </button>
        <input
          type="search"
          placeholder="Search conversations…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Search conversations"
          style={{
            width: '100%',
            marginTop: 8,
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid var(--ai-border)',
          }}
        />
      </div>
      <Spin spinning={!!loading} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={parentRef} style={{ flex: 1, overflow: 'auto', padding: '8px 10px' }}>
          {section === 'reports' ? (
            reports.length === 0 ? (
              <Empty description="No saved UDQs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              reports.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onSelectReport?.(r.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: 8,
                    fontSize: 13,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    borderRadius: 6,
                  }}
                >
                  <Text style={{ display: 'block' }}>{r.reportName}</Text>
                  {r.intent ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {r.intent}
                    </Text>
                  ) : null}
                </button>
              ))
            )
          ) : section === 'shared' ? (
            sharedConversations.length === 0 ? (
              <Empty description="No shared conversations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              sharedConversations.map((s) => (
                <AIConversationCard
                  key={s.resourceId}
                  id={s.resourceId}
                  title={s.title || 'Shared conversation'}
                  updatedAt={new Date().toISOString()}
                  mode="assistant"
                  onSelect={() => onSelectConversation(s.resourceId)}
                />
              ))
            )
          ) : list.length === 0 && !loading ? (
            <Empty description="No conversations yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {pinned.length > 0 ? (
                <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Pinned
                </Text>
              ) : null}
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map((vi) => {
                  const c = list[vi.index];
                  if (!c) return null;
                  return (
                    <div
                      key={c.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${vi.start}px)`,
                      }}
                    >
                      <AIConversationCard
                        id={c.id}
                        title={c.title}
                        updatedAt={c.updatedAt}
                        mode={c.mode}
                        pinned={c.pinned}
                        favorite={c.favorite}
                        active={c.id === activeId}
                        onSelect={() => onSelectConversation(c.id)}
                        onRename={() => onRename?.(c.id, c.title)}
                        onPin={() => onPin?.(c.id, !c.pinned)}
                        onFavorite={() => onFavorite?.(c.id, !c.favorite)}
                        onArchive={() => onArchive?.(c.id)}
                        onDuplicate={() => onDuplicate?.(c.id)}
                        onDelete={() => onDelete?.(c.id)}
                        onShare={() => onShare?.(c.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Spin>
    </aside>
  );
}
